/**
 * CheckinButton — one snippet, both modules.
 *
 * Where it's used:
 *   • /roadtrip → RoadtripDrivePage (traveller checks in from the car)
 *   • /finder   → FinderPoiDetailPage (visitor checks in at a listing)
 *   • Any other module can drop it in with a `<CheckinButton source=...
 *     targetKey=... />` — the backend endpoint is `/api/checkin/track`.
 *
 * Behaviour:
 *   1. Grabs navigator.geolocation (high-accuracy, 10 s timeout).
 *   2. POSTs to /api/checkin/track { source, key, lat, lng, note }.
 *   3. Toasts the reverse-geocoded address returned by the server and
 *      how many SMS subscribers were pinged.
 *
 * Owner rule #4: this is a GLOBAL SNIPPET. Do NOT clone it into a page.
 */
import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

export default function CheckinButton({
  source,             // "roadtrip" | "finder" | "custom"
  targetKey,          // trip_id | listing_id
  label   = "I'm here — text my group",
  compact = false,
  onSuccess,          // (payload) => void
  className = "",
  testid = "checkin-button",
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);

  const capture = async () => {
    if (busy) return;
    if (!source || !targetKey) {
      toast.error("Check-in target not set");
      return;
    }
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    setBusy(true);
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        });
      });
      const body = {
        source,
        key:      targetKey,
        lat:      pos.coords.latitude,
        lng:      pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        note:     note.trim() || null,
        silent:   false,
      };
      const r = await fetch(`${API}/api/checkin/track`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      if (j.skipped) {
        toast(`Already at ${j.place_name}`, {
          description: "Skipped duplicate check-in.",
        });
      } else {
        toast.success(`Checked in at ${j.place_name}`, {
          description: j.sms_sent
            ? `${j.sms_sent} SMS sent to your group.`
            : "No SMS subscribers configured yet.",
        });
      }
      setNote("");
      setShowNote(false);
      onSuccess?.(j);
    } catch (e) {
      toast.error(`Check-in failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const base = "inline-flex items-center gap-2 rounded-full font-semibold "
              + "transition-all disabled:opacity-60 disabled:cursor-not-allowed";
  const size = compact
    ? "px-3 h-8 text-sm"
    : "px-5 h-11 text-[15px]";

  return (
    <div className="hp-checkin" data-testid={`${testid}-root`}>
      <button
        type="button"
        onClick={capture}
        disabled={busy}
        data-testid={testid}
        className={`${base} ${size} bg-amber-400 hover:bg-amber-300 text-stone-900 ${className}`}
      >
        {busy
          ? <><Loader2 className="animate-spin" size={16}/> Locating…</>
          : <><MapPin size={16}/> {label}</>}
      </button>
      {!compact && (
        showNote ? (
          <div className="flex items-center gap-2 mt-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 140))}
              placeholder="Optional note (max 140 chars)"
              maxLength={140}
              data-testid={`${testid}-note`}
              className="flex-1 h-9 rounded-lg border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 text-sm"
            />
            <button
              type="button"
              onClick={() => { setShowNote(false); setNote(""); }}
              className="text-xs text-stone-500 hover:text-stone-700 dark:text-stone-400"
            >Cancel</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNote(true)}
            data-testid={`${testid}-add-note`}
            className="text-xs text-stone-500 hover:text-amber-500 mt-2 underline underline-offset-2"
          >+ Add note to this check-in</button>
        )
      )}
    </div>
  );
}
